import React, { useState, useEffect } from "react";
import { Rating, CircularProgress } from "@mui/material";
import Button from "@mui/material/Button";
import "@mui/icons-material";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import { styled } from "@mui/material/styles";
import { Box } from "@mui/material";
import { Menu, MenuItem } from "@mui/material";
import TextField from "@mui/material/TextField";
import axiosInstance from "../utils/axios";
import { useLoading } from "../context/LoadingContext";
import { useAuth } from "../context/AuthContextProvider";

// Override the default Rating styles with smaller size and custom color
const StableRating = styled(Rating)({
  "& .MuiRating-icon": {
    transition: "none",
    color: "#00555C", // Set default color
    fontSize: "1.2rem", // Smaller size
  },
  "& .MuiRating-iconEmpty": {
    color: "#00555C40", // Lighter version of the color for empty stars
  },
  "& .MuiRating-iconFilled": {
    color: "#00555C", // Color for filled stars
    transform: "none",
  },
  "& .MuiRating-iconHover": {
    color: "#00555C", // Color for hover state
    transform: "none",
  },
  "& .MuiRating-iconFocus": {
    transform: "none",
  },
});

const FeedbackMenu = ({ onClose, message }) => {
  const { showLoading, hideLoading } = useLoading();
  const [loading, setLoading] = useState(false);
  const { userId } = useAuth();
  const [ratings, setRatings] = useState({
    accuracy: 0,
    completeness: 0,
    nonbias: 0,
    humanlike: 0,
    explainability: 0,
    comments: "",
  });

  useEffect(() => {
    const fetchExistingFeedback = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(
          `/get_user_feedback?question_id=${
            message.question_id || ""
          }&session_id=${message.session_id || ""}&user_id=${userId || ""}`
        );
        // const response = await axiosInstance.get(`/get_user_feedback?${message.question_id || ''}/${message.session_id || ''}`);

        //console.log(response.data.feedback);
        if (response?.data?.feedback) {
          setRatings({
            accuracy: response.data.feedback?.accuracy,
            completeness: response.data.feedback?.completeness,
            nonbias: response.data.feedback?.nonbias,
            humanlike: response.data.feedback?.humanlike,
            explainability: response.data.feedback?.explainability,
            comments: response.data.feedback?.comments,
          });
        }
      } catch (error) {
        console.error("Error fetching existing feedback:", error);
      } finally {
        setLoading(false);
      }
    };

    if (message.session_id && message.question_id) {
      fetchExistingFeedback();
    }
  }, [message.session_id, message.question_id]);

  const handleRatingChange = (category, value) => {
    setRatings((prevRatings) => ({
      ...prevRatings,
      [category]: value,
    }));
  };

  const handleSubmit = async () => {
    //console.log("message", message);
    try {
      setLoading(true);
      const response = await axiosInstance.post("/submit_feedback", {
        accuracy: ratings.accuracy ? ratings.accuracy : 0,
        completeness: ratings.completeness ? ratings.completeness : 0,
        nonbias: ratings.nonbias ? ratings.nonbias : 0,
        humanlike: ratings.humanlike ? ratings.humanlike : 0,
        explainability: ratings.explainability ? ratings.explainability : 0,
        comments: ratings.comments ? ratings.comments : "",
        question_id: message.question_id || "",
        question: message.question || "",
        session_id: message.session_id || "",
        user_id: userId || "",
        response: message.response?.strDescription
          ? message.response?.strDescription
          : JSON.stringify(
              message?.response?.description?.map((item) => ({
                title: item.title,
                text: item.text,
              }))
            ) || "",
        //response: ""
      });
      console.log("Feedback submitted successfully:", ratings);
      onClose();
    } catch (error) {
      console.error("Error submitting feedback:", error);
      // You may want to show an error message to the user here
    } finally {
      onClose();
      setLoading(false);
    }
  };

  return (
    <div className="feedback-container bg-white px-4 py-2 rounded-lg shadow-md max-w-md mx-auto relative">
      {loading && (
        <div className="absolute inset-0 bg-gray-100/95 flex items-center justify-center rounded-lg z-50 cursor-not-allowed">
          <div className="flex flex-col items-center">
            <CircularProgress size={24} sx={{ color: "#00555C" }} />
            <span className="text-sm mt-2 text-gray-600">Loading...</span>
          </div>
        </div>
      )}
      <h2 className="text-sm font-bold mb-2">
        <BookmarkIcon color="primary" /> Feedback
      </h2>
      <p className="text-xs mb-1 ml-2">
        <i>Please provide your feedback</i>
      </p>
      <div className="flex items-center justify-between mb-1 text-xs ml-2">
        <label className="block font-semibold mb-1">
          <i>Accuracy</i>
        </label>
        <StableRating
          name="accuracy"
          value={ratings.accuracy}
          onChange={(event, newValue) =>
            handleRatingChange("accuracy", newValue)
          }
          disabled={loading}
        />
      </div>
      <div className="flex items-center justify-between mb-1 text-xs ml-2">
        <label className="block font-semibold mb-1">
          <i>Completeness</i>
        </label>
        <StableRating
          name="completeness"
          value={ratings.completeness}
          onChange={(event, newValue) =>
            handleRatingChange("completeness", newValue)
          }
          disabled={loading}
        />
      </div>
      <div className="flex items-center justify-between mb-1 text-xs ml-2">
        <label className="block font-semibold mb-1">
          <i>Non-Biasedness</i>
        </label>
        <StableRating
          name="nonbias"
          value={ratings.nonbias}
          onChange={(event, newValue) =>
            handleRatingChange("nonbias", newValue)
          }
          disabled={loading}
        />
      </div>
      <div className="flex items-center justify-between mb-1 text-xs ml-2">
        <label className="block font-semibold mb-1">
          <i>Human-like</i>
        </label>
        <StableRating
          name="humanlike"
          value={ratings.humanlike}
          onChange={(event, newValue) =>
            handleRatingChange("humanlike", newValue)
          }
          disabled={loading}
        />
      </div>
      <div className="flex items-center justify-between mb-1 text-xs ml-2">
        <label className="block font-semibold mb-1">
          <i>Explainability</i>
        </label>
        <StableRating
          name="explainability"
          value={ratings.explainability}
          onChange={(event, newValue) =>
            handleRatingChange("explainability", newValue)
          }
          disabled={loading}
        />
      </div>
      <div className="flex flex-col items-left justify-between mb-1 text-xs ml-2">
        <label className="block font-semibold mb-1">
          <i>Comments</i>
        </label>
        <TextField
          name="comments"
          placeholder="Enter your comments..."
          multiline
          rows={2}
          fullWidth
          size="small"
          value={ratings.comments || ""}
          onChange={(event) =>
            handleRatingChange("comments", event.target.value)
          }
          disabled={loading}
          sx={{
            fontSize: "12px",
            "& .MuiInputBase-input": {
              fontSize: "12px",
            },
          }}
        />
      </div>
      <Button
        variant="contained"
        color="primary"
        className="w-full mt-4"
        onClick={handleSubmit}
        disabled={loading}
        sx={{
          width: "100px",
          borderRadius: "24px",
          height: "20px",
          fontSize: "12px",
          marginLeft: "6px",
          backgroundColor: "#00555C", // Lighter color for disabled
        }}
      >
        Submit
      </Button>
    </div>
  );
};

export default FeedbackMenu;
